import {
    Button,
    Card,
    ErrorAlert,
    FormError,
    PasswordInput,
    TextInput,
    ThemeToggle,
    useTextForm,
} from '@lumlab/platform-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { authClient } from '../app/auth.ts';
import { BrandLogo } from '../app/BrandLogo.tsx';

/** Better Auth error statuses mapped to the platform's stable codes. */
function errorCode(status: number): string {
    if (status === 401) {
        return 'invalid_credentials';
    }
    if (status === 429) {
        return 'rate_limited';
    }
    return 'internal';
}

export function SignInPage() {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const [error, setError] = useState<{ code: string } | null>(null);
    const [pending, setPending] = useState(false);
    const form = useTextForm({ initialValues: { email: '', password: '' } });

    const submit = form.onSubmit(async (values) => {
        setPending(true);
        setError(null);
        const email =
            import.meta.env.DEV && ['user', 'admin'].includes(values.email.trim().toLowerCase())
                ? `${values.email.trim().toLowerCase()}@lumlab.test`
                : values.email.trim();
        try {
            const result = await authClient.signIn.email({ ...values, email });
            if (result.error) setError({ code: errorCode(result.error.status) });
        } catch {
            setError({ code: 'internal' });
        } finally {
            setPending(false);
        }
        // On success the session store refreshes, and the redirect below happens.
    });

    if (session.data) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="relative flex min-h-dvh flex-col items-center justify-center gap-8 p-4">
            <div className="absolute top-4 right-4">
                <ThemeToggle />
            </div>
            <BrandLogo layout="stacked" className="h-28 w-auto" />
            <Card padding="lg" className="w-full max-w-sm">
                <form onSubmit={submit}>
                    <div className="flex flex-col gap-4">
                        <FormError message={form.formError} />
                        <h1 className="text-2xl font-bold">{t('platform.auth.title')}</h1>
                        {import.meta.env.DEV && (
                            <p className="text-sm text-muted">{t('platform.auth.developmentAccess')}</p>
                        )}
                        {error && <ErrorAlert error={error} />}
                        <TextInput
                            type={import.meta.env.DEV ? 'text' : 'email'}
                            autoComplete={import.meta.env.DEV ? 'username' : 'email'}
                            required
                            label={import.meta.env.DEV ? t('platform.auth.identifier') : t('platform.auth.email')}
                            {...form.getInputProps('email')}
                        />
                        <PasswordInput
                            autoComplete="current-password"
                            required
                            label={t('platform.auth.password')}
                            {...form.getInputProps('password')}
                        />
                        <Button type="submit" loading={pending}>
                            {t('platform.auth.submit')}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
